import { useEffect, useState, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Cat, Heart, Calendar, Phone, Home, Menu, X, User, LogOut, 
  ChevronRight, Star, MapPin, Clock, PawPrint, Shield, Users,
  Mail, Send, CheckCircle, ArrowRight, Sparkles, Gift, Cookie,
  Trash2, Plus, CalendarDays, RefreshCw, Edit, Flag, Reply,
  Eye, EyeOff, MailOpen
} from "lucide-react";

import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { Calendar as CalendarComponent } from "./components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "./components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { Separator } from "./components/ui/separator";
import { ScrollArea } from "./components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";

// Import extracted components
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { API } from "./lib/api";
import ProfileCompletion from "./components/ProfileCompletion";

// Import extracted guide pages
import { 
  IndoorOutdoorPage, 
  MicrochippingPage, 
  ScanAngelsPage, 
  FoodGuidePage, 
  LitterGuidePage 
} from "./pages/GuidePages";

// Import Admin Page
import AdminPage from "./pages/AdminPageRefactored";

// Import Shop pages
import { ShopPage, ShopSuccessPage } from "./pages/ShopPages";

// Import Donation pages
import { DonatePage, DonationSuccessPage, DonationCancelPage } from "./pages/DonatePages";

// Import Auth pages
import { LoginPage, ForgotPasswordPage, ResetPasswordPage, AuthCallback } from "./pages/AuthPages";

// Import Legal pages
import { PrivacyPolicyPage, TermsPage } from "./pages/LegalPages";

// Import Cat pages
import { CatsPage, CatDetailPage } from "./pages/CatPages";

// Import Booking pages
import { BookPage, BookingSuccessPage } from "./pages/BookingPages";

// Import Content pages
import { AboutPage, ContactPage, ReviewsPage, DashboardPage, GalleryPage, BrandsPage } from "./pages/ContentPages";

// Import Blog pages
import { BlogPage, BlogPostPage, BlogSubmitPage } from "./pages/BlogPages";

// Import Lost & Found pages
import LostFoundPage from "./pages/LostFoundPages";

// Import Volunteer page
import VolunteerPage from "./pages/VolunteerPage";

// Import Social Media page
import SocialMediaPage from "./pages/SocialMediaPage";

// Import Hashtag Gallery page
import HashtagGalleryPage from "./pages/HashtagGalleryPage";

// Import In Memoriam page
import InMemoriamPage from "./pages/InMemoriamPage";

// Remove Emergent badge
const removeEmergentBadge = () => {
  const badge = document.getElementById('emergent-badge');
  if (badge) badge.remove();
  document.querySelectorAll('a[href*="emergent.sh"]').forEach(el => el.remove());
};
setInterval(removeEmergentBadge, 100);
setTimeout(() => clearInterval, 5000);

// Home Page
const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pt-36" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center bg-gradient-to-br from-stone-50 via-amber-50 to-stone-100 overflow-hidden">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-20 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div className="animate-fadeIn">
            <Badge className="bg-amber-100 text-amber-700 mb-6 font-medium">Norwich's Favourite Cat Service</Badge>
            <h1 className="text-5xl md:text-7xl font-bold text-stone-900 tracking-tight leading-tight mb-6">
              Where Every <span className="text-amber-600">Whisker</span> Finds a Home
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed mb-8 max-w-lg">
              CatPals+ is Norwich's trusted cat rescue and professional cat sitting service. 
              Give a cat their forever home, or let us care for yours while you're away.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button onClick={() => navigate("/cats")} className="bg-amber-600 hover:bg-amber-700 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1" data-testid="adopt-cta">
                <Heart className="w-5 h-5 mr-2" /> Adopt a Cat
              </Button>
              <Button onClick={() => navigate("/book")} variant="outline" className="border-2 border-stone-300 hover:border-stone-900 rounded-full px-8 py-6 text-lg font-medium" data-testid="book-cta">
                <Calendar className="w-5 h-5 mr-2" /> Book Cat Sitting
              </Button>
            </div>
          </div>
          <div className="relative animate-slideUp delay-200">
            <div className="relative w-full aspect-square max-w-lg mx-auto">
              <img 
                src="https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=800" 
                alt="Happy cat" 
                className="w-full h-full object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900">200+ Cats</p>
                    <p className="text-sm text-stone-500">Found loving homes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Our Purr-fect Services</h2>
            <p className="text-stone-600 max-w-2xl mx-auto">Whether you're looking to adopt or need trusted care for your feline friend</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="group bg-gradient-to-br from-amber-50 to-white border-amber-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden" data-testid="rescue-service-card">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-amber-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-stone-900 mb-3">Cat Rescue & Adoption</h3>
                <p className="text-stone-600 mb-6 leading-relaxed">
                  Every cat deserves a loving home. Browse our rescue cats and find your purr-fect companion. 
                  All our cats are health-checked, vaccinated, and ready for their forever family.
                </p>
                <Button onClick={() => navigate("/cats")} variant="link" className="p-0 text-amber-600 font-semibold group-hover:gap-3 transition-all">
                  Meet our cats <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
            <Card className="group bg-gradient-to-br from-green-50 to-white border-green-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden" data-testid="sitting-service-card">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-900 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-stone-900 mb-3">Professional Cat Sitting</h3>
                <p className="text-stone-600 mb-6 leading-relaxed">
                  Going away? Let our experienced cat lovers care for your furry friend. 
                  From daily visits to premium home stays, we've got you covered.
                </p>
                <Button onClick={() => navigate("/book")} variant="link" className="p-0 text-green-700 font-semibold group-hover:gap-3 transition-all">
                  View packages <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* All Cats Adopted Notice */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-2">Meet Our Cats</h2>
            <p className="text-stone-600">Finding forever homes for our feline friends</p>
          </div>
          <Card className="max-w-2xl mx-auto text-center p-10 bg-gradient-to-br from-amber-50 to-stone-50 border-amber-200">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-amber-600 fill-amber-600" />
            </div>
            <h3 className="text-2xl font-bold text-stone-900 mb-4">Wonderful News!</h3>
            <p className="text-stone-700 mb-4">
              All of the cats that needed new loving homes have been adopted!
            </p>
            <p className="text-stone-600 mb-6">
              We will update the page when more <span className="font-semibold text-amber-600">Fur Family</span> are available to adopt.
            </p>
            <Button onClick={() => navigate("/contact")} className="bg-amber-600 hover:bg-amber-700 rounded-full px-8">
              <Mail className="w-4 h-4 mr-2" /> Contact Us to Get Notified
            </Button>
          </Card>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-green-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <PawPrint className="absolute top-10 left-10 w-32 h-32" />
          <PawPrint className="absolute bottom-10 right-10 w-48 h-48" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { num: "200+", label: "Cats Rehomed" },
              { num: "500+", label: "Happy Customers" },
              { num: "16+", label: "Years Serving Norwich" },
              { num: "5", label: "Average Rating" }
            ].map(({ num, label }) => (
              <div key={label}>
                <p className="text-4xl md:text-5xl font-bold text-amber-400 mb-2">{num}</p>
                <p className="text-green-100">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Sparkles className="w-12 h-12 text-amber-600 mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">Ready to Make a Difference?</h2>
          <p className="text-lg text-stone-600 mb-8 max-w-2xl mx-auto">
            Whether you're looking to give a rescue cat their forever home or need trusted care 
            for your feline friend, we're here to help.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => navigate("/cats")} className="bg-amber-600 hover:bg-amber-700 rounded-full px-8 py-6 text-lg" data-testid="cta-adopt">
              <Heart className="w-5 h-5 mr-2" /> Adopt Today
            </Button>
            <Button onClick={() => navigate("/contact")} variant="outline" className="border-2 rounded-full px-8 py-6 text-lg" data-testid="cta-contact">
              <Phone className="w-5 h-5 mr-2" /> Get in Touch
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App Router
function AppRouter() {
  const location = useLocation();
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  // Check for session_id in hash BEFORE rendering routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/cats" element={<CatsPage />} />
      <Route path="/cats/:id" element={<CatDetailPage />} />
      <Route path="/book" element={<BookPage />} />
      <Route path="/booking/success" element={<BookingSuccessPage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/shop/success" element={<ShopSuccessPage />} />
      <Route path="/indoor-outdoor" element={<IndoorOutdoorPage />} />
      <Route path="/food-guide" element={<FoodGuidePage />} />
      <Route path="/litter-guide" element={<LitterGuidePage />} />
      <Route path="/donate" element={<DonatePage />} />
      <Route path="/donate/success" element={<DonationSuccessPage />} />
      <Route path="/donate/cancel" element={<DonationCancelPage />} />
      <Route path="/microchipping" element={<MicrochippingPage />} />
      <Route path="/scan-angels" element={<ScanAngelsPage />} />
      <Route path="/reviews" element={<ReviewsPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      <Route path="/brands" element={<BrandsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/submit" element={<BlogSubmitPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/volunteer" element={<VolunteerPage />} />
      <Route path="/lost-found" element={<LostFoundPage />} />
      <Route path="/social" element={<SocialMediaPage />} />
      <Route path="/community-gallery" element={<HashtagGalleryPage />} />
      <Route path="/in-memoriam" element={<InMemoriamPage />} />
    </Routes>
  );
}

// Wrapper to include ProfileCompletion modal
function AppContent() {
  const { showProfileCompletion, setShowProfileCompletion, completeProfile } = useAuth();
  
  return (
    <>
      <Header />
      <AppRouter />
      <Footer />
      <ProfileCompletion 
        isOpen={showProfileCompletion}
        onClose={() => setShowProfileCompletion(false)}
        onComplete={completeProfile}
      />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
